import { useEffect, useRef, useState } from "react";

/**
 * Defers mounting of below-the-fold sections until they scroll near the
 * viewport. Cuts initial main-thread JS execution (Total Blocking Time)
 * without changing any visual output.
 *
 * Uses a shared scroll/resize listener that re-checks every pending
 * section's real geometry (getBoundingClientRect) on each scroll event,
 * rather than relying purely on IntersectionObserver's per-element
 * entry callbacks. This matters because a fast/instant scroll (End key,
 * scrollbar drag, anchor-link jump) still fires a single "scroll" event
 * at the final settled position -- so a direct geometry check at that
 * moment reliably mounts every section that is now near the viewport,
 * even ones that were "jumped over" and never had an intermediate
 * intersection frame.
 */
const THRESHOLD_PX = 1000;
const pending = new Set();
let listenerAttached = false;

function checkPending() {
  if (pending.size === 0) return;
  const vh = window.innerHeight;
  let mountedOne = false;
  for (const entry of pending) {
    const el = entry.ref.current;
    if (!el) continue;
    const rect = el.getBoundingClientRect();
    if (rect.bottom > -THRESHOLD_PX && rect.top < vh + THRESHOLD_PX) {
      entry.setVisible(true);
      pending.delete(entry);
      mountedOne = true;
      break; // mount at most one section per frame -- avoids many motion
      // components initializing/animating simultaneously on a fast scroll,
      // which can otherwise trigger a framer-motion keyframe-resolver crash.
    }
  }
  if (mountedOne && pending.size > 0) {
    requestAnimationFrame(checkPending);
  }
}

function ensureListener() {
  if (listenerAttached) return;
  listenerAttached = true;
  let scheduled = false;
  const onScrollOrResize = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      checkPending();
    });
  };
  window.addEventListener("scroll", onScrollOrResize, { passive: true });
  window.addEventListener("resize", onScrollOrResize);
}

/**
 * Immediately mounts every still-pending deferred section. Used before a
 * programmatic jump (nav link / anchor click) so the destination's real
 * layout is in place first -- otherwise the browser would compute the
 * scroll offset against collapsed placeholders, and later mounting would
 * shift everything below, stranding the viewport on the wrong content.
 */
function mountAllPending() {
  pending.forEach((entry) => entry.setVisible(true));
  pending.clear();
}

/**
 * Smoothly scrolls to a section by id, mounting every deferred section
 * first so the final layout (and therefore the scroll offset) is stable.
 * Waits for two signals before scrolling, rather than trusting either
 * alone: (1) the target's DeferredMount placeholder has been swapped for
 * its real children (aria-hidden gone), and (2) the target already has
 * meaningful rendered height AND the overall document height has stopped
 * changing across consecutive frames -- this second check is what catches
 * a still-in-flight lazy-loaded child (Suspense fallback momentarily
 * rendering nothing) that the first check alone can't see.
 */
export function scrollToSection(id) {
  mountAllPending();
  let attempts = 0;
  let lastDocHeight = -1;
  let stableFrames = 0;
  const tryScroll = () => {
    attempts += 1;
    const el = document.getElementById(id);
    const docHeight = document.documentElement.scrollHeight;
    stableFrames = docHeight === lastDocHeight ? stableFrames + 1 : 0;
    lastDocHeight = docHeight;

    const targetReady = el && el.getAttribute("aria-hidden") !== "true" && el.offsetHeight > 40;
    const layoutSettled = stableFrames >= 3;
    const gaveUp = attempts > 300; // ~5s ceiling; scroll anyway rather than never

    if ((targetReady && layoutSettled) || gaveUp) {
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    requestAnimationFrame(tryScroll);
  };
  requestAnimationFrame(tryScroll);
}

export const DeferredMount = ({ children, minHeight = 400, id }) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (visible) return;
    ensureListener();
    const entry = { ref, setVisible };
    pending.add(entry);
    checkPending();
    return () => pending.delete(entry);
  }, [visible]);

  if (visible) return children;
  return <div ref={ref} id={id} style={{ minHeight }} aria-hidden="true" />;
};
